import { supabase } from '@/integrations/supabase/client';

/**
 * SECURITY NOTE: RLS (Row Level Security) Requirements
 * 
 * All functions in this module trust a caller-supplied userId parameter to filter/update rows.
 * When used from the browser with the anon Supabase client, an attacker could potentially pass
 * another user's userId and read/update their triggers unless RLS strictly enforces access control.
 * 
 * REQUIRED RLS POLICIES on gps_notification_triggers table:
 * - RLS must be enabled
 * - SELECT policy: Only allow users to read rows where user_id = auth.uid()
 * - INSERT/UPDATE policy: Only allow users to create/update rows where user_id = auth.uid()
 * - DELETE policy: Only allow users to delete rows where user_id = auth.uid()
 * 
 * Without proper RLS, these functions are vulnerable to cross-user data access.
 * Consider deriving userId from supabase.auth.getUser() server-side instead of taking it as a parameter.
 */

/**
 * Update user's location in GPS notification triggers
 * This updates all active GPS triggers for the user with their current location
 * 
 * SECURITY: Relies on RLS to prevent cross-user access. userId must match auth.uid()
 */
export async function updateUserLocation(
  userId: string,
  latitude: number,
  longitude: number
): Promise<void> {
  try {
    // Update all active GPS triggers for this user
    const { error } = await supabase
      .from('gps_notification_triggers')
      .update({
        user_latitude: latitude,
        user_longitude: longitude,
        updated_at: new Date().toISOString(),
      })
      .eq('user_id', userId)
      .eq('is_active', true);

    if (error) {
      console.error('Error updating user location:', error);
      throw new Error(`Failed to update location: ${error.message}`);
    }
  } catch (error) {
    console.error('Error in updateUserLocation:', error);
    throw error;
  }
}

/**
 * Create or update a GPS notification trigger for a specific truck
 * 
 * SECURITY: Relies on RLS to prevent cross-user access. userId must match auth.uid()
 */
export async function upsertGPSTrigger(
  userId: string,
  truckId: string,
  options: {
    latitude: number;
    longitude: number;
    triggerRadiusMiles?: number;
    notifyViaEmail?: boolean;
    notifyViaSMS?: boolean;
    notifyViaPush?: boolean;
    minHoursBetweenNotifications?: number;
  }
): Promise<{ id: string }> {
  try {
    const { data, error } = await supabase
      .from('gps_notification_triggers')
      .upsert({
        user_id: userId,
        truck_id: truckId,
        user_latitude: options.latitude,
        user_longitude: options.longitude,
        trigger_radius_miles: options.triggerRadiusMiles ?? 1.0,
        notify_via_email: options.notifyViaEmail ?? true,
        notify_via_sms: options.notifyViaSMS ?? false,
        notify_via_push: options.notifyViaPush ?? true,
        min_hours_between_notifications: options.minHoursBetweenNotifications ?? 24,
        is_active: true,
      }, {
        onConflict: 'user_id,truck_id',
      })
      .select('id')
      .single();

    if (error) {
      console.error('Error upserting GPS trigger:', error);
      throw new Error(`Failed to create GPS trigger: ${error.message}`);
    }

    if (!data) {
      throw new Error('No data returned from GPS trigger upsert');
    }

    return { id: data.id };
  } catch (error) {
    console.error('Error in upsertGPSTrigger:', error);
    throw error;
  }
}

/**
 * Get all active GPS triggers for a user
 * 
 * SECURITY: Relies on RLS to prevent cross-user access. userId must match auth.uid()
 */
export async function getUserGPSTriggers(userId: string) {
  try {
    const { data, error } = await supabase
      .from('gps_notification_triggers')
      .select(`
        *,
        food_trucks (
          id,
          name,
          cuisine_type,
          image_url
        )
      `)
      .eq('user_id', userId)
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching GPS triggers:', error);
      throw new Error(`Failed to fetch GPS triggers: ${error.message}`);
    }

    return data || [];
  } catch (error) {
    console.error('Error in getUserGPSTriggers:', error);
    throw error;
  }
}

/**
 * Deactivate a GPS trigger
 * 
 * SECURITY: Relies on RLS to prevent cross-user access. The trigger must belong to the authenticated user.
 * Note: This function uses triggerId instead of userId, but RLS should still enforce ownership.
 */
export async function deactivateGPSTrigger(triggerId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('gps_notification_triggers')
      .update({ is_active: false })
      .eq('id', triggerId);

    if (error) {
      console.error('Error deactivating GPS trigger:', error);
      throw new Error(`Failed to deactivate GPS trigger: ${error.message}`);
    }
  } catch (error) {
    console.error('Error in deactivateGPSTrigger:', error);
    throw error;
  }
}

